import { CreateFullPageStep } from '@carbon/ibm-products';
import { Checkbox } from '@mantine/core';
import { AddressElement, CardElement } from '@stripe/react-stripe-js';
import { MessageDescriptor, useIntl } from 'react-intl';

import { SubscriptionPlanDto } from 'api/dtos/subscriptionPlanDtos';
import InvoiceDetailsForm from 'Components/Common/InvoiceDetailsForm/InvoiceDetailsForm';
import type { InvoiceDetailsFormValues } from 'Components/Common/InvoiceDetailsForm/InvoiceDetailsForm';
import AddressForm from 'Components/Common/AddressForm/AddressForm';
import type { AddressFormValues } from 'Components/Common/AddressForm/AddressForm';
import SubscriptionPlanCard from 'Components/Common/SubscriptionPlanCard/SubscriptionPlanCard';
import sharedMessages from 'Components/utils/sharedMessages';

import styles from '../../InitialSetupModal.module.scss';
import PaymentInfoCard from './PaymentInfoCard/PaymentInfoCard';

type Props = {
    title: MessageDescriptor;
    description: MessageDescriptor;
    monthLabel: MessageDescriptor;
    choosePlanLabel: MessageDescriptor;
    selectedPlanLabel: MessageDescriptor;
    paymentTitle: MessageDescriptor;
    paymentDescription: MessageDescriptor;
    accountAddressTitle: MessageDescriptor;
    accountAddressDescription: MessageDescriptor;
    invoiceDetailsTitle: MessageDescriptor;
    invoiceDetailsDescription: MessageDescriptor;
    plans: SubscriptionPlanDto[];
    selectedPlanId: string;
    isDisabled: boolean;
    setAsDefaultPaymentMethod: boolean;
    address: AddressFormValues;
    invoiceDetailsSameAsAddress: boolean;
    invoiceDetails: InvoiceDetailsFormValues;
    showAddressValidationErrors: boolean;
    showInvoiceValidationErrors: boolean;
    onMount?: () => void;
    onSelectPlan: (planId: string) => void;
    onSetCardComplete: (value: boolean) => void;
    onSetBillingAddressComplete: (value: boolean) => void;
    onSetAsDefaultPaymentMethod: (value: boolean) => void;
    onAddressChange: (field: keyof AddressFormValues, value: string) => void;
    onInvoiceDetailsSameAsAddressChange: (value: boolean) => void;
    onInvoiceDetailsChange: (field: keyof InvoiceDetailsFormValues, value: string) => void;
};

const SubscriptionStep = ({
    title,
    description,
    monthLabel,
    choosePlanLabel,
    selectedPlanLabel,
    paymentTitle,
    paymentDescription,
    accountAddressTitle,
    accountAddressDescription,
    invoiceDetailsTitle,
    invoiceDetailsDescription,
    plans,
    selectedPlanId,
    isDisabled,
    setAsDefaultPaymentMethod,
    address,
    invoiceDetailsSameAsAddress,
    invoiceDetails,
    showAddressValidationErrors,
    showInvoiceValidationErrors,
    onMount,
    onSelectPlan,
    onSetCardComplete,
    onSetBillingAddressComplete,
    onSetAsDefaultPaymentMethod,
    onAddressChange,
    onInvoiceDetailsSameAsAddressChange,
    onInvoiceDetailsChange,
}: Props) => {
    const intl = useIntl();

    return (
        <CreateFullPageStep
            title={intl.formatMessage(title)}
            fieldsetLegendText=""
            disableSubmit={isDisabled}
            onMount={onMount}
        >
            <div className={styles.outerContainer}>
                <div className={styles.stepContainer}>
                    <p>{intl.formatMessage(description)}</p>
                    <div className={styles.planGrid}>
                        {plans.map((plan) => (
                            <SubscriptionPlanCard
                                key={plan.id}
                                plan={plan}
                                highlighted={selectedPlanId === plan.id}
                                monthLabel={intl.formatMessage(monthLabel)}
                                actionLabel={selectedPlanId === plan.id
                                    ? intl.formatMessage(selectedPlanLabel)
                                    : intl.formatMessage(choosePlanLabel)}
                                actionVariant={selectedPlanId === plan.id ? 'filled' : 'light'}
                                onCardClick={() => { onSelectPlan(plan.id); }}
                                onAction={() => { onSelectPlan(plan.id); }}
                            />
                        ))}
                    </div>

                    <div className={styles.paymentSection}>
                        <h4 className={styles.sectionTitle}>{intl.formatMessage(paymentTitle)}</h4>
                        <p>{intl.formatMessage(paymentDescription)}</p>

                        <div className={styles.infoLayout}>
                            <div className={styles.infoLayoutContent}>
                                <div className={styles.paymentFields}>
                                    <div>
                                        <div className={styles.paymentFieldTitle}>
                                            {intl.formatMessage(sharedMessages.card)}
                                        </div>
                                        <div className={styles.stripeElementBox}>
                                            <CardElement
                                                options={{
                                                    hidePostalCode: true,
                                                }}
                                                onChange={(event) => {
                                                    onSetCardComplete(event.complete);
                                                }}
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <div className={styles.paymentFieldTitle}>
                                            {intl.formatMessage(sharedMessages.billingAddress)}
                                        </div>
                                        <div className={styles.stripeAddressBox}>
                                            <AddressElement
                                                options={{
                                                    mode: 'billing',
                                                    allowedCountries: ['PL'],
                                                }}
                                                onChange={(event) => {
                                                    onSetBillingAddressComplete(event.complete);
                                                }}
                                            />
                                        </div>
                                    </div>
                                </div>

                                <Checkbox
                                    id="initial-setup-default-payment-method"
                                    classNames={{
                                        label: styles.setupCheckboxLabel,
                                        input: styles.setupCheckboxInput,
                                    }}
                                    label={intl.formatMessage(sharedMessages.defaultPaymentMethod)}
                                    checked={setAsDefaultPaymentMethod}
                                    onChange={(event) => {
                                        onSetAsDefaultPaymentMethod(event.currentTarget.checked);
                                    }}
                                />
                            </div>
                        </div>
                    </div>

                    <div className={styles.invoiceDetailsSection}>
                        <h4 className={styles.sectionTitle}>{intl.formatMessage(accountAddressTitle)}</h4>
                        <p>{intl.formatMessage(accountAddressDescription)}</p>
                        <AddressForm
                            values={address}
                            onChange={onAddressChange}
                            showValidationErrors={showAddressValidationErrors}
                            fieldClassNames={{
                                label: styles.setupFieldLabel,
                                input: styles.setupFieldInput,
                            }}
                        />
                    </div>

                    <div className={styles.invoiceDetailsSection}>
                        <h4 className={styles.sectionTitle}>{intl.formatMessage(invoiceDetailsTitle)}</h4>
                        <p>{intl.formatMessage(invoiceDetailsDescription)}</p>
                        <Checkbox
                            id="initial-setup-invoice-same-as-address"
                            classNames={{
                                label: styles.setupCheckboxLabel,
                                input: styles.setupCheckboxInput,
                            }}
                            label={intl.formatMessage(messages.invoiceSameAsAddress)}
                            checked={invoiceDetailsSameAsAddress}
                            onChange={(event) => {
                                onInvoiceDetailsSameAsAddressChange(event.currentTarget.checked);
                            }}
                        />
                        {!invoiceDetailsSameAsAddress && (
                            <InvoiceDetailsForm
                                values={invoiceDetails}
                                onChange={onInvoiceDetailsChange}
                                markRequiredFields
                                showValidationErrors={showInvoiceValidationErrors}
                                fieldClassNames={{
                                    label: styles.setupFieldLabel,
                                    input: styles.setupFieldInput,
                                }}
                            />
                        )}
                    </div>
                </div>
                <PaymentInfoCard />

            </div>
        </CreateFullPageStep>
    );
};

const messages = {
    invoiceSameAsAddress: {
        id: 'src.Screens.Dashboard.InitialSetup.SubscriptionStep.invoiceSameAsAddress',
        defaultMessage: 'Dane do faktury takie same jak dane osoby',
    },
};

export default SubscriptionStep;
